import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Loader2, ExternalLink, Sparkles, Clock, CheckCircle2,
  XCircle, AlertCircle, FileText, Image as ImageIcon,
  ChevronDown, Cpu, GitCompare, ListOrdered,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';


const STORY_STATUS = {
  pending:        { colour: 'bg-slate-100 text-slate-600 border-slate-300',           label: 'Not generated',  icon: Clock },
  in_progress:    { colour: 'bg-blue-50 text-blue-600 border-blue-200',               label: 'Generating',     icon: Loader2 },
  complete:       { colour: 'bg-emerald-50 text-emerald-700 border-emerald-200',      label: 'Story created',  icon: CheckCircle2 },
  failed:         { colour: 'bg-red-50 text-red-600 border-red-200',                  label: 'Failed',         icon: XCircle },
  rewrite_failed: { colour: 'bg-amber-50 text-amber-700 border-amber-200',            label: 'Rewrite failed', icon: AlertCircle },
};

const QUEUED_BADGE = {
  colour: 'bg-blue-50 text-blue-700 border-blue-200',
  label:  'Queued',
  icon:   ListOrdered,
};
const fmtUK = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', {
    timeZone: 'Europe/London',
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });
};

const FILTERS = [
  { id: 'all',            key: 'all',            label: 'All' },
  { id: 'pending',        key: 'pending',        label: 'Not generated' },
  { id: 'queued',         key: 'queued',         label: 'Queue' },
  { id: 'complete',       key: 'complete',       label: 'Generated' },
  { id: 'rewrite_failed', key: 'rewrite_failed', label: 'Failed' },
];


function ItemRow({ item, models, onGenerate, onView }) {
  const [busy, setBusy]         = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef                 = useRef(null);
    const canGenerate = ['pending', 'rewrite_failed', 'failed'].includes(item.story_creation_status);

  const cfg  = item.is_queued
    ? QUEUED_BADGE
    : (STORY_STATUS[item.story_creation_status] || STORY_STATUS.pending);
  const Icon = cfg.icon;

  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  const fire = async (modelIds) => {
    setBusy(true);
    setMenuOpen(false);
    try { await onGenerate(item, modelIds); } finally { setBusy(false); }
  };

  return (
    <div className={`border rounded-xl p-4 transition-colors shadow-sm ${
      item.is_queued
        ? 'bg-blue-50/40 border-blue-200 hover:border-blue-300'
        : 'bg-white border-slate-200 hover:border-slate-300'
    }`}>
      <div className="flex items-start gap-3">
        {item.source_image_url && (
          <img src={item.source_image_url} alt=""
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
            className="w-20 h-14 object-cover rounded shrink-0 bg-slate-100" />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-2 mb-1">
            <h3 className="text-slate-900 font-medium leading-snug flex-1">{item.title}</h3>
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-semibold border ${cfg.colour} shrink-0`}>
              <Icon size={10} className={item.story_creation_status === 'in_progress' && !item.is_queued ? 'animate-spin' : ''} />
              {cfg.label}
            </span>
          </div>
          {item.summary && <p className="text-xs text-slate-500 line-clamp-2">{item.summary}</p>}
          <div className="flex items-center gap-3 mt-2 text-[11px] text-slate-400 flex-wrap">
            <span className="text-slate-500">{item.feed_source_name}</span>
            {item.source_published_at && <span>{fmtUK(item.source_published_at)}</span>}
            {item.output_count > 0 && (
              <span className="text-emerald-600 inline-flex items-center gap-1">
                <FileText size={11} /> {item.output_count} {item.output_count === 1 ? 'story' : 'stories'}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <a href={item.source_url} target="_blank" rel="noopener noreferrer"
            className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded transition-colors">
            <ExternalLink size={14} />
          </a>
          <button onClick={() => onView(item)}
            className="px-3 py-1.5 text-xs text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded transition-colors">
            Details
          </button>
          {canGenerate && (
            <div className="relative flex items-stretch" ref={menuRef}>
              <button
                onClick={() => fire(null)}
                disabled={busy}
                className={`flex items-center gap-1.5 disabled:opacity-50 text-white pl-3 pr-2.5 py-1.5 rounded-l text-xs font-medium transition-colors border-r ${
                  item.is_queued
                    ? 'bg-amber-600 hover:bg-amber-700 border-amber-500/40'
                    : 'bg-blue-600 hover:bg-blue-700 border-blue-500/40'
                }`}
                title={item.is_queued ? 'Generate now, ahead of the queue' : 'Generate with default model'}
              >
                {busy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                {item.is_queued ? 'Generate now' : 'Generate'}
              </button>
              <button
                onClick={() => setMenuOpen(o => !o)}
                disabled={busy || models.length === 0}
                className={`disabled:opacity-50 text-white px-1.5 py-1.5 rounded-r text-xs transition-colors flex items-center ${
                  item.is_queued ? 'bg-amber-600 hover:bg-amber-700' : 'bg-blue-600 hover:bg-blue-700'
                }`}
                title="Choose model"
              >
                <ChevronDown size={12} className={menuOpen ? 'rotate-180 transition-transform' : 'transition-transform'} />
              </button>
              {menuOpen && models.length > 0 && (
                <div className="absolute right-0 top-full mt-1 z-20 bg-white border border-slate-200 rounded-lg shadow-xl py-1 min-w-[200px]">
                  <p className="text-[10px] uppercase tracking-wider text-slate-400 px-3 py-1.5 font-semibold">Generate with</p>
                  {models.map(m => (
                    <button
                      key={m.id}
                      onClick={() => fire([m.id])}
                      className="w-full text-left px-3 py-2 text-xs text-slate-600 hover:bg-slate-50 hover:text-slate-900 flex items-center gap-2"
                    >
                      <Cpu size={11} className="text-slate-400" />
                      {m.label}
                    </button>
                  ))}
                  {models.length >= 2 && (
                    <>
                      <div className="border-t border-slate-200 my-1" />
                      <button
                        onClick={() => fire(models.map(m => m.id))}
                        className="w-full text-left px-3 py-2 text-xs text-purple-600 hover:bg-purple-50 flex items-center gap-2"
                      >
                        <GitCompare size={11} />
                        Compare all models
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


export default function PublicationItemsTab({ pubId }) {
  const navigate = useNavigate();
  const [items, setItems]       = useState([]);
  const [total, setTotal]       = useState(0);
  const [counts, setCounts]     = useState({});
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('');
  const [sort, setSort]         = useState('source_desc');
  const [offset, setOffset]     = useState(0);
  const [sources, setSources]   = useState([]);
  const [models, setModels]     = useState([]);
  const limit = 25;

  useEffect(() => { setOffset(0); }, [search, statusFilter, sourceFilter, sort]);
  useEffect(() => { load(); }, [search, statusFilter, sourceFilter, sort, offset, pubId]);
  useEffect(() => { loadSources(); loadModels(); }, [pubId]);

  const load = async () => {
    setLoading(true);
    try {
      const params = { publication_id: pubId, limit, offset, sort };
      if (search)       params.search = search;
      if (sourceFilter) params.source_id = sourceFilter;
      // Queue is a job state rather than an item state, so it filters on
      // its own parameter instead of story_creation_status.
      if (statusFilter === 'queued')   params.queued = true;
      else if (statusFilter !== 'all') params.story_status = statusFilter;
      const { data } = await api.get('/feeds/items', { params });
      setItems(data.items);
      setTotal(data.total);
      setCounts(data.counts || {});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load items');
    } finally {
      setLoading(false);
    }
  };

  const loadSources = async () => {
    try {
      const { data } = await api.get(`/feeds/publications/${pubId}/subscriptions`);
      const unique = [];
      const seen = new Set();
      (data || []).forEach(s => {
        if (!seen.has(s.feed_source_id)) {
          seen.add(s.feed_source_id);
          unique.push({ id: s.feed_source_id, name: s.source_name });
        }
      });
      setSources(unique);
    } catch {
    }
  };

  const loadModels = async () => {
    try {
      const { data } = await api.get('/feeds/models');
      setModels(data.models || []);
    } catch {
    }
  };

  const handleGenerate = async (item, modelIds = null) => {
    try {
      const payload  = modelIds && modelIds.length > 0 ? { model_ids: modelIds } : {};
      const { data } = await api.post(`/feeds/items/${item.id}/generate-stories`, payload);
      const created    = data.stories_created || 0;
      const failed     = data.failed || 0;
      const modelsUsed = (data.models_used || []).length;

      if (created > 0 && failed === 0) {
        const tag = modelsUsed > 1 ? ` across ${modelsUsed} models` : '';
        toast.success(`Generated ${created} ${created === 1 ? 'story' : 'stories'}${tag}`);
      } else if (created > 0 && failed > 0) {
        toast.warning(`Generated ${created}, failed ${failed}`);
      } else if (failed > 0) {
        const firstError = data.results?.find(r => !r.success)?.error || 'Generation failed';
        toast.error(firstError);
      }
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Generation failed');
    }
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search items..."
            className="w-full pl-9 pr-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-blue-500" />
        </div>

        <select
          value={sourceFilter}
          onChange={e => setSourceFilter(e.target.value)}
          className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:border-blue-500"
        >
          <option value="">All sources</option>
          {sources.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>

        <select
          value={sort}
          onChange={e => setSort(e.target.value)}
          className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:border-blue-500"
        >
          <option value="source_desc">Newest by source date</option>
          <option value="source_asc">Oldest by source date</option>
          <option value="fetched_desc">Newest by fetched date</option>
          <option value="fetched_asc">Oldest by fetched date</option>
        </select>

        <div className="flex gap-1 bg-slate-100 p-1 rounded-lg border border-slate-200">
          {FILTERS.map(t => (
            <button key={t.id} onClick={() => setStatusFilter(t.id)}
              className={`px-3 py-1.5 rounded text-xs font-medium transition-colors inline-flex items-center gap-1.5 ${
                statusFilter === t.id
                  ? 'bg-white text-blue-600 border border-slate-200 shadow-sm'
                  : 'text-slate-500 hover:text-slate-900'
              }`}>
              {t.label}
              {counts[t.key] > 0 && (
                <span className={`text-[10px] font-bold rounded-full px-1.5 py-0.5 min-w-[18px] text-center ${
                  statusFilter === t.id ? 'bg-blue-50 text-blue-600' : 'bg-slate-200 text-slate-600'
                }`}>
                  {counts[t.key]}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12 gap-2 text-slate-400 text-sm">
          <Loader2 size={14} className="animate-spin" /> Loading items...
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 text-slate-400 text-sm">
          {statusFilter === 'queued'
            ? 'Nothing for this publication is waiting in the queue.'
            : 'No items available. Subscribe to a source and fetch items.'}
        </div>
      ) : (
        <>
          <p className="text-xs text-slate-400 mb-3">
            Showing {offset + 1}–{Math.min(offset + items.length, total)} of {total} · times in UK
          </p>
          <div className="space-y-2">
            {items.map(item => (
              <ItemRow
                key={item.id}
                item={item}
                models={models}
                onGenerate={handleGenerate}
                onView={(i) => navigate(`/feeds/items/${i.id}`)}
              />
            ))}
          </div>
          {total > limit && (
            <div className="flex items-center justify-center gap-3 mt-5">
              <button onClick={() => setOffset(Math.max(0, offset - limit))} disabled={offset === 0}
                className="px-3 py-1.5 bg-white hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed text-slate-600 text-sm rounded border border-slate-200">
                Previous
              </button>
              <span className="text-xs text-slate-400">
                Page {Math.floor(offset / limit) + 1} of {Math.ceil(total / limit)}
              </span>
              <button onClick={() => setOffset(offset + limit)} disabled={offset + limit >= total}
                className="px-3 py-1.5 bg-white hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed text-slate-600 text-sm rounded border border-slate-200">
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}